/**
 * Concrete implementation of `MapServiceBase` which provides a wrapper around *external* map services - those loaded from ArcGIS servers.
 *
 *
 * @module app/models/map/ExternalMapService
 */
import MapServiceBase from "app/models/map/base/MapServiceBase";
import _ from "underscore";
import $ from "jquery";
import FeatureLayer from "app/models/map/FeatureLayer";
import async from "async";
import RequestUtils from "app/utils/RequestUtils";
import Field from "core/models/Field";
import urlService from "app/UrlService";
import messenger from "app/Messenger";
import session from "app/Session";

export default MapServiceBase.extend({
  /**
   * Initializes an object.
   * @param  {object} options The options hash.
   * @param {MapService} options.service The map service to associated with this object.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      service: null,
    });

    /**
     * The map service associated with this object.
     * @type {MapService}
     */
    this.service = this.options.service;

    /**
     * The most recent token data retrieved from the server for this service.
     * @type {Boolean}
     */
    this.tokenData = false;
    this._childLayers = [];
  },

  _tokenTimeout: 0,

  /**
   * Refresh the token associated with this service.
   * @return {Promise} A promise which resolves with authentication data or rejects with an error.
   */
  refreshToken: function () {
    var deferred = new $.Deferred();
    var self = this;
    var esri = window.require("esri/kernel"),
      IdentityManagerBase = window.require("esri/IdentityManagerBase");

    window.require("esri/IdentityManager");

    function attemptTokenRefresh(cb) {
      var deferred = new $.Deferred();
      var serviceUrl = new URL(self.service.get("url"));
      var arctokenUrl = urlService.apiurl("arctoken/" + self.service.id, true);

      if (serviceUrl.host == "hbk-arcgis-prod") {
        arctokenUrl = `https://hbk-arcgis-prod/node/chartroom-api-arctokens/${self.service.id}`;
      }

      RequestUtils.post(
        arctokenUrl,
        function (data) {
          if (data.token) {
            _.each(["", "/arcgis"], function (suffix) {
              var cred = new IdentityManagerBase.Credential();
              var oldIdx = false;

              cred.expires = data.expiration;
              cred.resources = [data.url];
              cred.scope = "server";
              cred.server = data.server + suffix;
              cred.ssl = true;
              cred.token = data.token;
              cred.userId = data.user;
              cred.serviceId = self.service.id;
              cred.suffix = suffix;
              //remove the previous credential if it exists
              _.each(esri.id.credentials, function (credential, key) {
                if (
                  credential.serviceId == self.service.id &&
                  credential.suffix == suffix
                ) {
                  oldIdx = key;
                }
              });

              if (oldIdx !== false) {
                esri.id.credentials[oldIdx] = cred;
              } else {
                esri.id.credentials.push(cred);
              }
            });
          }

          self.tokenData = data;

          deferred.resolve(data);
        },
        "json"
      ).fail(function (err) {
        deferred.reject(err);
      });

      deferred
        .done(function (result) {
          cb(null, result);
        })
        .fail(function (err) {
          cb(err);
        });
    }

    async.retry(
      {
        times: 3,
        interval: 500, //milliseconds
      },
      attemptTokenRefresh,
      function (err, result) {
        if (err) {
          deferred.reject(err);
        } else {
          if (self._tokenTimeout) {
            clearTimeout(self._tokenTimeout);
          }

          setTimeout(function () {
            self.trigger("token-timeout");
          }, (result.expiration - Date.now() / 1000) * 1000);

          deferred.resolve(result);
        }
      }
    );

    return deferred.promise();
  },

  load: function () {
    var refresh = this.refreshToken();
    var deferred = new $.Deferred();
    var self = this;
    var manager = window.require("esri/IdentityManager");
    manager.on("dialog-create", function () {
      _.defer(function () {
        manager.dialog.hide();
        manager.dialog.destroy();
      });
    });

    refresh.done(function (data) {
      function attemptLoad(cb) {
        var deferred = new $.Deferred();
        var ArcGISTiledMapServiceLayer = window.require(
          "esri/layers/ArcGISDynamicMapServiceLayer"
        );
        var topic = window.require("dojo/topic");

        var layer = (self._layer = new ArcGISTiledMapServiceLayer(data.url));

        layer.on("load", function () {
          //build a tree of layers.
          //first make sure everything is keyed to ID
          var layerIndex = {};

          _.each(layer.layerInfos, function (layerInfo) {
            layerIndex[layerInfo.id] = layerInfo;
          });

          function processNode(info, siblings, list) {
            var options = {
              serviceLayer: self,
              info: info,
              name: info.name,
            };

            if (info.subLayerIds && info.subLayerIds.length) {
              //recursive case
              options.children = [];
              _.each(info.subLayerIds, function (subLayerId) {
                processNode(list[subLayerId], options.children, list);
              });
            } else {
              //base case
              options.url = data.url + "/" + info.id;
              options.visible = info.defaultVisibility;
              options.snapshot = self.service.get("snapshot");
            }

            siblings.push(new FeatureLayer(options));
          }

          //now build up the tree using processNode
          var children = [];

          _.each(layerIndex, function (layerInfo) {
            if (layerInfo.parentLayerId === -1) {
              processNode(layerInfo, children, layerIndex);
            }
          });

          self._childLayers = children;
          deferred.resolve(self);
        });
        layer.on("error", function (err) {
          deferred.reject(err);
        });

        var handler = topic.subscribe("esri.Error", function (err) {
          deferred.reject(err);
        });

        deferred
          .done(function (result) {
            cb(null, result);
          })
          .fail(function (err) {
            cb(err);
          })
          .always(function () {
            handler.remove();
          });
      }

      async.retry(
        {
          times: 3,
          interval: 500, //milliseconds
        },
        attemptLoad,
        function (err, result) {
          if (err) {
            deferred.reject(err, self);
          } else {
            deferred.resolve(result);

            self.stopListening();
            self.listenTo(session, "authenticate:success", self.refreshToken);
            self.listenTo(self, "token-timeout", self.refreshToken);
          }
        }
      );
    });

    return $.when(deferred, refresh);
  },

  getLayers: function () {
    return this._childLayers;
  },

  createLayerField: function () {
    return new Field(
      {
        id: "layer",
        name: "layer",
        label: "Layer",
        values: this.getLayerNames(),
        editable: false,
        filterable: true,
        sortable: true,
      },
      {
        getter: function (model) {
          var originalLayer = model.originalLayer || model.get("originalLayer");

          if (originalLayer) {
            return String(
              (originalLayer.featureLayer || originalLayer.info).id
            );
          }
        },
        setter: function () {
          messenger.error(
            "Moving features in external services is not supported."
          );
        },
      }
    );
  },

  getName: function () {
    return this.service.get("name");
  },

  getMaxRecordCount: function () {
    return 1000;
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/ExternalMapService.js
// module id = 510
// module chunks = 1